'use client';

/**
 * MobileShell — Phase 1.
 *
 * Composition:
 *   - Top bar row 1: hamburger (opens Explorer drawer), product + active mode
 *     label, language toggle, dev user switcher.
 *   - Top bar row 2: horizontal-scroll mode chips fed by `useNavItems()` —
 *     identical data to the desktop ActivityBar, so role-filtered modes
 *     appear in both shells automatically.
 *   - Body: Canvas only — Explorer and Copilot ride on top as overlays.
 *   - Bottom tab bar: Explorer (→ drawer), Canvas (→ dismiss overlays),
 *     Copilot (→ sheet). Active highlight tracks overlay state.
 *   - MobileExplorerDrawer: left-edge slide-in; auto-closes on selection.
 *   - MobileCopilotSheet: bottom slide-up; closes on user action only.
 *
 * Overlay coupling: the layout store holds a single `mobileActiveOverlay`
 * slot (`'explorer' | 'copilot' | null`). Opening one automatically closes
 * the other — no stack, no explicit coupling.
 *
 * The shell does NOT import `ResizablePanelGroup`. The resizable tree is a
 * desktop primitive — on mobile one body pane is visible at a time.
 *
 * Next PRs layer:
 *   - PR 2 — touch-target + type scale pass (global)
 *   - PR 6 — gesture composition (swipe-in drawer, swipe-down sheet)
 *
 * @component_role: Mobile Shell
 * @component_type: Layout Container
 * @domain: Frontend Shell
 * @layer: presentation
 */

import { useState, useEffect } from 'react';
import { useTheme } from 'next-themes';
import {
  Bot,
  BookOpen,
  ClipboardPen,
  Compass,
  Factory,
  FileText,
  FlaskConical,
  FolderKanban,
  HelpCircle,
  Home,
  LayoutDashboard,
  Layers,
  Menu,
  MessageSquare,
  Moon,
  Package,
  Settings,
  Sparkles,
  Sun,
  Target,
  User,
  type LucideIcon,
} from 'lucide-react';

import { Canvas } from '@/components/canvas/canvas';
import { DevUserSwitcher } from '@/components/dev/dev-user-switcher';
import { LanguageToggle } from './language-toggle';
import { MobileExplorerDrawer } from './mobile-explorer-drawer';
import { MobileCopilotSheet } from './mobile-copilot-sheet';
import { cn } from '@/lib/utils/cn';
import {
  useLayoutStore,
  type ExplorerView,
  type MobileActiveOverlay,
} from '@/lib/stores/layout-store';
import { switchExplorerMode } from '@/lib/stores/tab-store';
import { useNavItems } from '@/lib/stores/total-matrix-store';

// Mirror of ActivityBar's icon map — keep in sync until we extract a shared helper.
const ICON_MAP: Record<string, LucideIcon> = {
  Home,
  Sparkles,
  FolderKanban,
  Factory,
  FileText,
  Package,
  LayoutDashboard,
  User,
  Settings,
  BookOpen,
  FlaskConical,
  Target,
  Bot,
  ClipboardPen,
  MessageSquare,
  Compass,
};

function getIcon(iconName: string): LucideIcon {
  return ICON_MAP[iconName] || HelpCircle;
}

// Bottom-bar tabs. Explorer + Copilot taps set the active overlay;
// Canvas taps dismiss whatever is open.
type BottomTab = {
  id: 'explorer' | 'canvas' | 'copilot';
  label: string;
  Icon: LucideIcon;
  overlay: MobileActiveOverlay; // value to set — null for Canvas (dismiss)
};

const BOTTOM_TABS: BottomTab[] = [
  { id: 'explorer', label: 'Explorer', Icon: Layers, overlay: 'explorer' },
  { id: 'canvas', label: 'Canvas', Icon: LayoutDashboard, overlay: null },
  { id: 'copilot', label: 'Copilot', Icon: MessageSquare, overlay: 'copilot' },
];

export function MobileShell() {
  const {
    activeExplorerView,
    mobileActiveOverlay,
    setMobileActiveOverlay,
  } = useLayoutStore();
  const navItems = useNavItems();
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  // Same mode-switch semantics as the desktop ActivityBar — both delegate
  // to the one shared switchExplorerMode (tab-store.ts), which is the
  // actual source of truth now, not duplicated here.
  const handleModeSwitch = (mode: ExplorerView) => switchExplorerMode(mode);

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-background">
      {/* Top bar row 1: hamburger + product + controls. */}
      <div className="h-10 flex items-center gap-2 px-2 border-b border-border bg-card">
        <button
          type="button"
          onClick={() => setMobileActiveOverlay('explorer')}
          aria-label="Open explorer"
          aria-expanded={mobileActiveOverlay === 'explorer'}
          className="p-1.5 -ml-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
        >
          <Menu className="w-5 h-5" />
        </button>
        <span className="text-sm font-medium text-foreground shrink-0">Klyde</span>
        <span className="text-xs text-muted-foreground capitalize shrink-0">
          · {activeExplorerView}
        </span>
        <div className="ml-auto flex items-center gap-1 shrink-0">
          <LanguageToggle />
          <button
            onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
            className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
            aria-label="Toggle theme"
            suppressHydrationWarning
          >
            {mounted ? (
              resolvedTheme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />
            ) : (
              <Sun className="w-4 h-4 opacity-0" />
            )}
          </button>
          <DevUserSwitcher />
        </div>
      </div>

      {/* Top bar row 2: horizontal-scroll mode chips */}
      <nav
        aria-label="Modes"
        className="h-11 border-b border-border bg-card flex items-stretch overflow-x-auto"
      >
        {navItems.map((item) => {
          const Icon = getIcon(item.icon);
          const active = activeExplorerView === item.mode;
          return (
            <button
              key={item.mode}
              type="button"
              onClick={() => handleModeSwitch(item.mode)}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'shrink-0 px-3 flex items-center gap-1.5 text-xs transition-colors border-b-2',
                active
                  ? 'text-foreground border-primary'
                  : 'text-muted-foreground border-transparent',
              )}
            >
              <Icon className="w-4 h-4" aria-hidden="true" />
              <span className="truncate max-w-[7rem]">{item.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Body — Canvas is the only persistent body pane. */}
      <div className="flex-1 min-h-0 overflow-auto">
        <Canvas />
      </div>

      {/* Bottom tab bar — sets the active overlay (null = just Canvas). */}
      <nav
        aria-label="Panes"
        className="min-h-14 border-t border-border bg-card flex items-stretch pb-[env(safe-area-inset-bottom)]"
      >
        {BOTTOM_TABS.map((tab) => {
          const active = mobileActiveOverlay === tab.overlay;
          const Icon = tab.Icon;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setMobileActiveOverlay(tab.overlay)}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'flex-1 flex flex-col items-center justify-center gap-0.5 text-[11px] transition-colors border-t-2',
                active
                  ? 'text-foreground border-primary'
                  : 'text-muted-foreground border-transparent',
              )}
            >
              <Icon className="w-5 h-5" aria-hidden="true" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Overlays — at most one is visible at a time (store-enforced). */}
      <MobileExplorerDrawer />
      <MobileCopilotSheet />
    </div>
  );
}
